// ---------------------------------------------------------------------------
// The deployment experience a bundle runs with: which UX template, and what this bundle changes
// about it.
//
// CHOSEN IN A DIALOG, NOT A DROP-DOWN. A template's name says almost nothing about what it does to
// the machine — "R6 always-interactive" and "Notify + 2 defers" are only distinguishable by their
// settings — so picking one from a <select> means choosing blind and opening the editor to find
// out. The dialog lists each one with the summary of what it will show, which is the thing being
// chosen between.
//
// ADJUSTING OPENS THE REAL WIZARD, in the apply mode it already has for adjusting one deployment
// without touching the template. A reduced set of fields here would be a second, worse editor for
// the same object, and the two would drift the first time a control was added to one of them.
//
// What comes back from apply mode is a COMPLETE config, so the bundle's own adjustments are
// recovered by comparing it with the template (utils/uxOverrides). Storing the delta is what keeps
// the reference live: a template edit reaches every bundle that has not overridden that setting.
// ---------------------------------------------------------------------------
import { useEffect, useState } from 'react'
import { CheckCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline'

import { listDeploymentTemplates, type DeploymentTemplate } from '../services/api'
import { Modal } from './ui'
import { uxSummaryFromConfig } from '../utils/templateSummary'
import { mergedConfig, overrideCount, overridesFrom } from '../utils/uxOverrides'
import UxTemplateWizard from './UxTemplateWizard'

export interface BundleExperienceValue {
  templateId: string | null
  overrides: any | null
}

const SILENT_SUMMARY = 'Installs with no prompts, no countdown and no branding.'

export default function BundleExperienceField({
  value,
  onChange,
  disabled = false,
}: {
  value: BundleExperienceValue
  onChange: (next: BundleExperienceValue) => void
  disabled?: boolean
}) {
  const [templates, setTemplates] = useState<DeploymentTemplate[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [choosing, setChoosing] = useState(false)

  useEffect(() => {
    listDeploymentTemplates()
      .then((r) => setTemplates(r.templates || []))
      .catch(() => setLoadError('Could not load your deployment experiences.'))
  }, [])

  const selected = templates.find((t) => t.id === value.templateId) ?? null
  const merged = selected ? mergedConfig(selected.config_json, value.overrides) : null
  const changed = overrideCount(value.overrides)

  return (
    <>
      <button
        type="button"
        onClick={() => setChoosing(true)}
        disabled={disabled}
        className="flex w-full items-start gap-3 rounded-lg border border-line bg-panel3/50 px-3 py-2.5 text-left hover:border-accent/50 disabled:opacity-60"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm text-ftext">
              {selected ? selected.name : 'Silent — no prompts, no branding'}
            </span>
            {changed > 0 && (
              <span className="shrink-0 text-xs text-accent">
                · {changed} adjusted
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-fmuted">
            {merged ? uxSummaryFromConfig(merged) : SILENT_SUMMARY}
          </p>
        </div>
        <span className="shrink-0 text-xs text-accent">Change</span>
      </button>

      {loadError && <p className="mt-1.5 text-xs text-red-600 dark:text-red-300">{loadError}</p>}

      {choosing && (
        <ExperienceChooser
          templates={templates}
          value={value}
          onChange={onChange}
          onClose={() => setChoosing(false)}
        />
      )}
    </>
  )
}

function ExperienceChooser({
  templates,
  value,
  onChange,
  onClose,
}: {
  templates: DeploymentTemplate[]
  value: BundleExperienceValue
  onChange: (next: BundleExperienceValue) => void
  onClose: () => void
}) {
  // Edited in the dialog and only handed back on Done, so Cancel really cancels — including an
  // adjustment made through the wizard, which would otherwise be applied the moment it was saved.
  const [draft, setDraft] = useState<BundleExperienceValue>(value)
  const [adjusting, setAdjusting] = useState(false)

  const selected = templates.find((t) => t.id === draft.templateId) ?? null
  const merged = selected ? mergedConfig(selected.config_json, draft.overrides) : null
  const changed = overrideCount(draft.overrides)

  const pick = (id: string | null) => {
    // Adjustments belong to the template they were made against, so switching templates drops
    // them rather than reapplying settings the author chose somewhere else.
    if (id === draft.templateId) return
    setDraft({ templateId: id, overrides: null })
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Deployment experience"
        size="wide"
        footer={
          <>
            <button className="btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-green"
              onClick={() => {
                onChange(draft)
                onClose()
              }}
            >
              Done
            </button>
          </>
        }
      >
        <p className="mb-3 text-sm text-fmuted">
          What the machine shows while this bundle runs. Every app in the bundle deploys with it.
        </p>

        <div className="space-y-2">
          <Choice
            name="Silent"
            summary={SILENT_SUMMARY}
            selected={draft.templateId === null}
            onSelect={() => pick(null)}
          />
          {templates.map((t) => (
            <Choice
              key={t.id}
              name={t.name}
              badge={t.scope === 'global' ? 'provided' : undefined}
              summary={uxSummaryFromConfig(
                t.id === draft.templateId ? merged : t.config_json,
              )}
              selected={t.id === draft.templateId}
              onSelect={() => pick(t.id)}
              onAdjust={t.id === draft.templateId ? () => setAdjusting(true) : undefined}
              adjustedCount={t.id === draft.templateId ? changed : 0}
              onReset={
                t.id === draft.templateId && changed > 0
                  ? () => setDraft({ ...draft, overrides: null })
                  : undefined
              }
            />
          ))}
        </div>

        {templates.length === 0 && (
          <p className="mt-3 text-xs text-ffaint">
            No deployment experiences yet. Create one under Settings → Templates, or leave this
            bundle silent.
          </p>
        )}

        {selected && (
          <p className="mt-4 text-xs text-ffaint">
            Text and colours travel with the bundle. A custom banner image reaches a machine only
            through a branding package, so a machine without one shows the default artwork.
          </p>
        )}
      </Modal>

      {/* The wizard is a PAGE, not a dialog: it renders bare and expects its host to supply the
          chrome (Templates.tsx renders it inline; the deploy wizard hosts it inside its own modal).
          Rendered without one here it landed behind the chooser's overlay and Adjust did nothing
          visible. `full` is the size that exists for a wizard with a side-by-side preview. */}
      {adjusting && selected && (
        <Modal
          open
          onClose={() => setAdjusting(false)}
          title={`Adjust ${selected.name} for this bundle`}
          size="full"
        >
          <p className="mb-3 text-sm text-fmuted">
            Changes here apply to this bundle only. The template itself is not touched, so every
            other bundle and deployment using it keeps its settings.
          </p>
          <UxTemplateWizard
            // Opened on the template AS THIS BUNDLE DEPLOYS IT, so an author adjusts what they are
            // looking at rather than starting again from the template's own settings.
            template={{ ...selected, config_json: merged }}
            onClose={() => setAdjusting(false)}
            onSaved={() => setAdjusting(false)}
            onApply={(config: any) => {
              setDraft({
                templateId: selected.id,
                // Compared against the TEMPLATE, not against what this bundle already overrode:
                // the delta has to say what differs from the template, or a setting reverted by
                // hand would stay recorded as an override forever.
                overrides: nullIfEmpty(overridesFrom(selected.config_json, config)),
              })
              setAdjusting(false)
            }}
          />
        </Modal>
      )}
    </>
  )
}

function Choice({
  name,
  summary,
  selected,
  onSelect,
  onAdjust,
  onReset,
  adjustedCount = 0,
  badge,
}: {
  name: string
  summary: string
  selected: boolean
  onSelect: () => void
  onAdjust?: () => void
  onReset?: () => void
  adjustedCount?: number
  badge?: string
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        selected ? 'border-accent bg-accent/5' : 'border-line hover:border-line2'
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          aria-pressed={selected}
        >
          <CheckCircleIcon
            className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-accent' : 'text-ffaint'}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm text-ftext">{name}</span>
              {badge && <span className="shrink-0 text-[11px] text-ffaint">({badge})</span>}
              {adjustedCount > 0 && (
                <span className="shrink-0 text-[11px] text-accent">
                  {adjustedCount} adjusted
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-fmuted">{summary}</p>
          </div>
        </button>
        {onAdjust && (
          <button type="button" className="btn-outline btn-sm shrink-0" onClick={onAdjust}>
            <PencilSquareIcon className="h-4 w-4" /> Adjust
          </button>
        )}
      </div>
      {onReset && (
        <button type="button" className="mt-1.5 ml-6 text-xs text-accent underline" onClick={onReset}>
          Reset to the template
        </button>
      )}
    </div>
  )
}

const nullIfEmpty = (o: Record<string, any>) => (Object.keys(o).length > 0 ? o : null)
